class ResultSet {
  constructor () {
    this.source = null;
    /** @type {Array<string>} */
    this.categories = [];
    /** @type {Record<string, import('./Result').default[]>} */
    this.results = {};
    /** @type {Record<string, ResultSetMapping>} */
    this.mappings = {};
  }

  /**
   * @param {string} source
   */
  setSource (source) {
    this.source = source;
  }

  /**
   * @param {string} category
   * @param {import('./Result').default} result
   */
  addResult (category, result) {
    // if object has a spatial mapping to a category we already have...
    // attempt to merge this object into existing result from gis layer
    if (
      result.mapping &&
      this.results.hasOwnProperty(result.mapping.LayerName)
    ) {
      const id = result.feature.attributes[result.mapping.CamaField];
      const results = this.results[result.mapping.LayerName];

      for (let i = 0; i < results.length; i++) {
        const r = results[i];

        if (
          !this.isGISCamaKeyMatched(
            r.feature.attributes[result.mapping.LayerField],
            id,
            result.mapping.LayerField
          )
        ) {
          continue;
        }

        // merge the attributes and spatial mapping
        // from the search controller into result from gis layer
        const oldAttributes = r.feature.attributes;

        // keep GlobalID if it exists
        if (oldAttributes.hasOwnProperty('GlobalID')) {
          result.feature.attributes['GlobalID'] = oldAttributes['GlobalID'];
        }

        r.feature.attributes = result.feature.attributes;
        r.mapping = result.mapping;
        r.merged = true;

        // don't need to add in its own category now
        return;
      }
    }

    // check if result set has given category, and add it if it doesn't
    const found = this.categories.includes(category);

    if (!found) {
      this.categories.push(category);
      this.results[category] = [];
    }

    this.results[category].push(result);
  }

  cleanResults () {
    this.cleanParcels();
  }

  /**
   * @private
   * Removes the extra parcels the ESRI find task gets when searching for PIDs
   * with a decimal in the name.
   *
   * ESRI Find task converts the full string into the smallest representation
   * and does a wild card search on that.
   *
   * I.E. for stanton county 0000181.00 will be transformed into 181
   * then wild card searched, returning results for 0001181.00, 0001810.01, etc.
   */
  cleanParcels () {
    const results = this.results['Parcels'];

    if (!results) {
      return;
    }

    let hasMerged = false;
    let hasDecimal = false;
    const cleanedArray = [];
    const decimalRegexp = /^\d+\.\d+$/;

    for (let j = 0; j < results.length; j++) {
      const res = results[j];

      hasDecimal = hasDecimal || decimalRegexp.test(res.displayName);

      if (res.merged) {
        hasMerged = true;

        cleanedArray.push(res);
      }
    }

    // Remove the extra results ESRI might have erroneously found.
    if (hasDecimal && hasMerged) {
      this.results['Parcels'] = cleanedArray;
    }

    // If the results contain merged results,
    // make sure the headers in the grid use it instead of an unmerged one.
    if (hasMerged && !this.results['Parcels'].merged) {
      this.moveMergedParcelToTop();
    }
  }

  cleanPrivateParcels (privateParcels) {
    const results = this.results['Parcels'];

    if (!results) {
      return;
    }

    for (let j = 0; j < results.length; j++) {
      const pid = results[j].displayName;

      if (Object.values(privateParcels).includes(pid)) {
        results[j].feature.attributes['LegalDesc'] = '';
      }
    }
  }

  /**
   * @private
   */
  moveMergedParcelToTop () {
    let i = 0;
    let merged = this.results['Parcels'][i].merged;

    while (!merged) {
      i++;
      merged = this.results['Parcels'][i].merged;
    }

    const mergedObject = this.results['Parcels'][i];
    this.results['Parcels'].splice(i, 1);
    this.results['Parcels'].unshift(mergedObject);
  }

  /**
   * @param {string} category
   * @param {ResultSetMapping} [mapping]
   */
  addMapping (category, mapping = null) {
    const found = this.categories.includes(category);

    if (!found) {
      this.categories.push(category);
      this.results[category] = [];
    }

    this.mappings[category] = mapping;
  }

  isEmpty () {
    for (let i = 0; i < this.categories.length; i++) {
      const cat = this.categories[i];

      const res = this.results[cat];

      if (res.length > 0) {
        return false;
      }
    }

    return true;
  }

  numResults () {
    let numFound = 0;

    for (let i = 0; i < this.categories.length; i++) {
      const cat = this.categories[i];

      const res = this.results[cat];
      numFound += res.length;
    }

    return numFound;
  }

  /**
   * @private
   * @param {any} gisKey
   * @param {any} camaKey
   * @param {string} layerField
   */
  isGISCamaKeyMatched (gisKey, camaKey, layerField) {
    let gKey = gisKey || '';
    let cKey = camaKey || '';

    if (layerField && layerField.toLowerCase() === 'pid') {
      gKey = gKey.toString().trim().replace('.', '');
      cKey = cKey.toString().trim().replace('.', '');
    }

    return gKey === cKey;
  }
}

/**
 * @typedef {object} ResultSetMapping
 * @property {string} CamaField
 * @property {string} LayerName
 * @property {string} LayerField
 */

export default ResultSet;
